<template>
  <div class="basic-settings-container">
    <div class="settings-card">
      <div class="card-header">
        <h3 class="card-title">{{ $t('ddnsSetting.basicSettings') }}</h3>
      </div>
      <div class="card-body">
        <div class="table-responsive">
          <table class="settings-table">
            <thead>
              <tr>
                <th>{{ $t('ddnsSetting.publicIpCheckCycle') }}</th>
                <th>{{ $t('ddnsSetting.status') }}</th>
                <th>{{ $t('common.actions') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td :data-label="$t('ddnsSetting.checkCycle')">{{ settingsData.checkIPTime }}</td>
                <td :data-label="$t('ddnsSetting.status')">
                  <span class="status-badge" :class="settingsData.switch ? 'running' : 'stopped'">
                    {{ settingsData.switch ? $t('ddnsSetting.running') : $t('ddnsSetting.stopped') }}
                  </span>
                </td>
                <td :data-label="$t('common.actions')" class="action-cell">
                  <div class="action-buttons">
                    <el-button
                      v-if="settingsData.switch"
                      type="warning"
                      size="small"
                      @click="stopDnStatus"
                    >
                      {{ $t('ddnsSetting.stopService') }}
                    </el-button>
                    <template v-else>
                      <el-button
                        type="success"
                        size="small"
                        @click="startDnStatus(settingsData)"
                      >
                        {{ $t('ddnsSetting.startService') }}
                      </el-button>
                      <el-button
                        type="primary"
                        size="small"
                        @click="showEditDialog"
                      >
                        {{ $t('ddnsSetting.edit') }}
                      </el-button>
                    </template>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- 编辑设置对话框 -->
    <el-dialog
      v-model="editDialogVisible"
      :title="$t('ddnsSetting.modifyCheckCycle')"
      :width="isMobile ? '95%' : '500px'"
      :destroy-on-close="true"
      class="settings-dialog"
    >
      <el-form :model="editForm" :rules="formRules" ref="editFormRef" label-position="top">
        <el-form-item :label="$t('ddnsSetting.publicIpCheckCycle')" prop="checkIPTime">
          <el-input
            v-model.number="editForm.checkIPTime"
            type="number"
            :placeholder="$t('ddnsSetting.inputCheckCycle')"
            :min="1"
            :max="99999"
          />
        </el-form-item>
      </el-form>
      
      <template #footer>
        <div class="dialog-footer">
          <el-button @click="editDialogVisible = false">{{ $t('ddnsSetting.cancel') }}</el-button>
          <el-button type="primary" @click="updateDnStatus">{{ $t('ddnsSetting.save') }}</el-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { ElMessage, ElMessageBox } from 'element-plus'
import { API_BASE_URL } from "@/config"

export default {
  name: 'BasicSettings',
  setup() {
    const { t } = useI18n()

    // 移动端检测
    const isMobile = ref(false)

    const checkMobile = () => {
      isMobile.value = window.innerWidth < 768
    }

    // 数据状态
    const settingsData = ref({
      switch: false,
      checkIPTime: 60
    })

    // 编辑表单
    const editForm = ref({
      checkIPTime: 60
    })

    const editFormRef = ref(null)
    const editDialogVisible = ref(false)

    // 表单验证规则
    const formRules = {
      checkIPTime: [
        { required: true, message: t('ddnsSetting.inputCheckCycle'), trigger: 'blur' },
        { type: 'number', min: 1, max: 99999, message: t('ddnsSetting.inputCheckCycle'), trigger: 'blur' }
      ]
    }

    // 获取DNS状态
    const fetchDnsStatus = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/get-dns-status`, {
          method: 'GET',
          credentials: 'include'
        })

        if (!response.ok) {
          throw new Error(t('ddnsSetting.fetchFailed'))
        }

        const result = await response.json()

        if (result.errCode === 1000) {
          ElMessage.error(t('ddnsSetting.pleaseLoginFirst'))
          return
        }

        if (result.errCode != 0) {
          ElMessage.error(result.errMsg)
          return
        }

        settingsData.value = {
          switch: result.data.switch,
          checkIPTime: result.data.checkIPTime
        }

      } catch (error) {
        ElMessage.error(error.message || t('ddnsSetting.getSettingsFailed'))
      }
    }
    
    // 显示编辑对话框
    const showEditDialog = () => {
      editForm.value = {
        checkIPTime: settingsData.value.checkIPTime
      }
      editDialogVisible.value = true
    }
    
    // 更新DNS配置（不启动服务）
    const updateDnStatus = async () => {
      try {
        await editFormRef.value.validate()

        const params = new URLSearchParams()
        params.append('checkIPTime', editForm.value.checkIPTime)

        const response = await fetch(`${API_BASE_URL}/update-dns-status`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          credentials: 'include',
          body: params
        })

        if (!response.ok) {
          throw new Error(t('ddnsSetting.updateFailed'))
        }

        const data = await response.json()
        ElMessage.success(data.errMsg || t('ddnsSetting.updateSuccess'))
        editDialogVisible.value = false
        fetchDnsStatus()
      } catch (error) {
        ElMessage.error(error.message || t('ddnsSetting.updateFailed'))
      }
    }

    // 启动DNS服务
    const startDnStatus = async (row) => {
      try {
        await ElMessageBox.confirm(t('ddnsSetting.confirmStart'), t('common.confirm'), {
          confirmButtonText: t('common.confirm'),
          cancelButtonText: t('common.cancel'),
          type: 'warning'
        })

        const params = new URLSearchParams()
        params.append('checkIPTime', row.checkIPTime)

        const response = await fetch(`${API_BASE_URL}/update-dns-status`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          credentials: 'include',
          body: params
        })

        if (!response.ok) {
          throw new Error(t('ddnsSetting.startFailed'))
        }

        const data = await response.json()
        ElMessage.success(data.errMsg || t('ddnsSetting.serviceStarted'))
        fetchDnsStatus()
      } catch (error) {
        if (error !== 'cancel') {
          ElMessage.error(error.message || t('ddnsSetting.startFailed'))
        }
      }
    }

    // 停止DNS服务
    const stopDnStatus = async () => {
      try {
        await ElMessageBox.confirm(t('ddnsSetting.confirmStop'), t('common.confirm'), {
          confirmButtonText: t('common.confirm'),
          cancelButtonText: t('common.cancel'),
          type: 'warning'
        })

        const response = await fetch(`${API_BASE_URL}/stop-dns-status`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          credentials: 'include',
          body: ''
        })

        if (!response.ok) {
          throw new Error(t('ddnsSetting.stopFailed'))
        }

        const data = await response.json()
        ElMessage.success(data.errMsg || t('ddnsSetting.serviceStopped'))
        fetchDnsStatus()
      } catch (error) {
        if (error !== 'cancel') {
          ElMessage.error(error.message || t('ddnsSetting.stopFailed'))
        }
      }
    }
    
    // 窗口大小变化处理
    const handleResize = () => {
      checkMobile()
    }
    
    // 初始化
    onMounted(() => {
      checkMobile()
      window.addEventListener('resize', handleResize)
      fetchDnsStatus()
    })

    onUnmounted(() => {
      window.removeEventListener('resize', handleResize)
    })
    
    return {
      t,
      isMobile,
      settingsData,
      editForm,
      editFormRef,
      editDialogVisible,
      formRules,
      showEditDialog,
      updateDnStatus,
      startDnStatus,
      stopDnStatus
    }
  }
}
</script>

<style scoped lang="scss">
.basic-settings-container {
  padding: 0px;
  max-width: 100%;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    padding: 0 12px;
    width: 100%;
  }
  
  @media (max-width: 480px) {
    padding: 0 8px;
  }
}

.settings-card {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  margin-top: 20px;
  width: 100%;
  box-sizing: border-box;
  
  .card-header {
    padding: 20px;
    border-bottom: 1px solid #ebeef5;
    
    .card-title {
      font-size: 18px;
      font-weight: 600;
      color: #303133;
      margin: 0;
    }
  }
  
  .card-body {
    padding: 20px;
  }
  
  @media (max-width: 768px) {
    margin-top: 12px;
    
    .card-header {
      padding: 12px 16px;
      
      .card-title {
        font-size: 16px;
      }
    }
    
    .card-body {
      padding: 12px;
    }
  }
  
  @media (max-width: 480px) {
    margin-top: 8px;
    
    .card-header {
      padding: 10px 12px;
      
      .card-title {
        font-size: 15px;
      }
    }
    
    .card-body {
      padding: 8px;
    }
  }
}

.table-responsive {
  overflow-x: auto;
  width: 100%;
  
  @media (max-width: 768px) {
    width: 100%;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
}

.settings-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
  
  th, td {
    padding: 12px 15px;
    text-align: left;
    border-bottom: 1px solid #ebeef5;
  }
  
  th {
    background-color: #f5f7fa;
    color: #909399;
    font-weight: 600;
    white-space: nowrap;
  }
  
  td {
    color: #606266;
    vertical-align: middle;
  }
  
  tr:hover td {
    background-color: #f5f7fa;
  }
  
  @media (max-width: 768px) {
    width: 100%;
    min-width: 100%;
    display: block;
    
    thead {
      display: none;
    }
    
    tbody, tr {
      width: 100%;
      display: block;
    }
    
    tr {
      margin-bottom: 0;
      border: none;
      border-radius: 0;
      background: #fff;
    }
    
    td {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 10px 0;
      border-bottom: 1px solid #ebeef5;
      width: 100%;
      box-sizing: border-box;
      
      &:before {
        content: attr(data-label);
        font-weight: 600;
        color: #909399;
        margin-right: 12px;
        flex-shrink: 0;
        min-width: 80px;
        font-size: 13px;
      }
      
      &:last-child {
        border-bottom: none;
      }
      
      &.action-cell {
        padding: 10px 0;
        
        &:before {
          align-self: flex-start;
          padding-top: 4px;
        }
      }
    }
  }
  
  @media (max-width: 480px) {
    td {
      padding: 8px 0;
      font-size: 12px;
      
      &:before {
        min-width: 70px;
        font-size: 12px;
      }
    }
  }
}

.action-cell {
  .action-buttons {
    display: flex;
    gap: 8px;
    flex-wrap: nowrap;
    
    .el-button {
      flex-shrink: 0;
    }
    
    .el-button + .el-button {
      margin-left: 0;
    }
  }
  
  @media (max-width: 768px) {
    .action-buttons {
      justify-content: flex-end;
    }
    
    .el-button--small {
      padding: 6px 12px;
      font-size: 12px;
    }
  }
  
  @media (max-width: 480px) {
    .action-buttons {
      gap: 4px;
      
      .el-button--small {
        padding: 5px 8px;
        font-size: 11px;
      }
    }
  }
}

.status-badge {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  
  &.running {
    background-color: #f0f9eb;
    color: #67c23a;
  }
  
  &.stopped {
    background-color: #f5f5f5;
    color: #909399;
  }
  
  @media (max-width: 768px) {
    padding: 3px 6px;
    font-size: 11px;
  }
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  
  @media (max-width: 480px) {
    flex-direction: column;
    
    .el-button {
      width: 100%;
      margin-left: 0 !important;
    }
  }
}

/* Element Plus 对话框移动端适配 */
.settings-dialog {
  :deep(.el-dialog) {
    @media (max-width: 768px) {
      width: 95% !important;
      margin-top: 5vh !important;
    }
    
    @media (max-width: 480px) {
      width: 100% !important;
      height: auto !important;
      margin: 0 !important;
      max-height: 100%;
      border-radius: 0;
      position: fixed;
      bottom: 0;
      left: 0;
      right: 0;
      margin-bottom: 0 !important;
      
      .el-dialog__header {
        padding: 12px 16px;
        border-bottom: 1px solid #ebeef5;
      }
      
      .el-dialog__body {
        padding: 16px;
        max-height: calc(80vh - 120px);
        overflow-y: auto;
      }
      
      .el-dialog__footer {
        padding: 12px 16px;
        border-top: 1px solid #ebeef5;
      }
    }
  }
}

/* Element Plus 组件适配 */
:deep(.el-input__inner) {
  @media (max-width: 768px) {
    font-size: 13px;
    height: 32px;
    line-height: 32px;
  }
  
  @media (max-width: 480px) {
    font-size: 12px;
    height: 30px;
    line-height: 30px;
  }
}

:deep(.el-form-item__label) {
  @media (max-width: 768px) {
    font-size: 13px;
    padding-bottom: 4px;
  }
  
  @media (max-width: 480px) {
    font-size: 12px;
  }
}

:deep(.el-button--small) {
  @media (max-width: 768px) {
    padding: 6px 12px;
    font-size: 12px;
  }
  
  @media (max-width: 480px) {
    padding: 5px 8px;
    font-size: 11px;
  }
}
</style>